import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './ProfilePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, sessionToken, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [profileData, setProfileData] = useState({
    name: '',
    age: '',
    gender: '',
    favorite_seasons: [],
    height: '',
    weight: '',
    shirt_size: '',
    pant_size: '',
    shoe_size: '',
    dob_month: '',
    dob_day: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        age: user.age || '',
        gender: user.gender || '',
        favorite_seasons: user.favorite_seasons || [],
        height: user.height || '',
        weight: user.weight || '',
        shirt_size: user.shirt_size || '',
        pant_size: user.pant_size || '',
        shoe_size: user.shoe_size || '',
        dob_month: user.dob_month || '',
        dob_day: user.dob_day || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSeasonToggle = (season) => {
    setProfileData(prev => {
      const seasons = prev.favorite_seasons || [];
      if (seasons.includes(season)) {
        return {
          ...prev,
          favorite_seasons: seasons.filter(s => s !== season)
        };
      } else {
        return {
          ...prev,
          favorite_seasons: [...seasons, season]
        };
      }
    });
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const updateData = {
        name: profileData.name,
        age: profileData.age ? parseInt(profileData.age) : null,
        gender: profileData.gender || null,
        favorite_seasons: profileData.favorite_seasons,
        height: profileData.height || null,
        weight: profileData.weight || null,
        shirt_size: profileData.shirt_size || null,
        pant_size: profileData.pant_size || null,
        shoe_size: profileData.shoe_size || null,
        dob_month: profileData.dob_month ? parseInt(profileData.dob_month) : null,
        dob_day: profileData.dob_day ? parseInt(profileData.dob_day) : null
      };

      const response = await axios.put(
        `${API}/auth/profile`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Profile updated successfully!');
        setEditing(false);
        // Refresh user data
        await refreshUser();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const seasons = [
    { value: 'winter', label: '❄️ Winter', emoji: '❄️' },
    { value: 'spring', label: '🌸 Spring', emoji: '🌸' },
    { value: 'summer', label: '☀️ Summer', emoji: '☀️' },
    { value: 'fall', label: '🍂 Fall', emoji: '🍂' }
  ];

  return (
    <Layout>
      <div className="profile-page">
        <div className="profile-container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your personal information and preferences</p>
        </div>

        <div className="profile-content">
          {/* Profile Card */}
          <div className="profile-card">
            <div className="profile-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name} />
              ) : (
                <div className="avatar-placeholder">
                  {user?.name?.charAt(0).toUpperCase() || '👤'}
                </div>
              )}
            </div>

            <div className="profile-info-section">
              <h2>Personal Information</h2>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="info-grid">
                <div className="info-item">
                  <label>Full Name</label>
                  {editing ? (
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                    />
                  ) : (
                    <p>{user?.name || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Email</label>
                  <p>{user?.email}</p>
                  <span className="info-note">Email cannot be changed</span>
                </div>

                <div className="info-item">
                  <label>Age</label>
                  {editing ? (
                    <input
                      type="number"
                      name="age"
                      value={profileData.age}
                      onChange={handleInputChange}
                      placeholder="Enter your age"
                      min="1"
                      max="120"
                    />
                  ) : (
                    <p>{user?.age || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Gender</label>
                  {editing ? (
                    <select
                      name="gender"
                      value={profileData.gender}
                      onChange={handleInputChange}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="unisex">Prefer not to say</option>
                      <option value="kids">Kids</option>
                    </select>
                  ) : (
                    <p>{user?.gender || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Date of Birth</label>
                  {editing ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <select
                        name="dob_month"
                        value={profileData.dob_month}
                        onChange={handleInputChange}
                        style={{ flex: 1 }}
                      >
                        <option value="">Month</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                      <input
                        type="number"
                        name="dob_day"
                        value={profileData.dob_day}
                        onChange={handleInputChange}
                        placeholder="Day"
                        min="1"
                        max="31"
                        style={{ flex: 1 }}
                      />
                    </div>
                  ) : (
                    <p>
                      {user?.dob_month && user?.dob_day 
                        ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][user.dob_month - 1]} ${user.dob_day}`
                        : 'Not set'}
                    </p>
                  )}
                </div>

                <div className="info-item">
                  <label>Height</label>
                  {editing ? (
                    <input
                      type="text"
                      name="height"
                      value={profileData.height}
                      onChange={handleInputChange}
                      placeholder="e.g., 5'10 or 178 cm"
                    />
                  ) : (
                    <p>{user?.height || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Weight</label>
                  {editing ? (
                    <input
                      type="text"
                      name="weight"
                      value={profileData.weight}
                      onChange={handleInputChange}
                      placeholder="e.g., 165 lbs or 75 kg"
                    />
                  ) : (
                    <p>{user?.weight || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Shirt Size (US)</label>
                  {editing ? (
                    <select
                      name="shirt_size"
                      value={profileData.shirt_size}
                      onChange={handleInputChange}
                    >
                      <option value="">Select size</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="XXL">XXL</option>
                      <option value="XXXL">XXXL</option>
                    </select>
                  ) : (
                    <p>{user?.shirt_size || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Pant Size (US)</label>
                  {editing ? (
                    <input
                      type="text"
                      name="pant_size"
                      value={profileData.pant_size}
                      onChange={handleInputChange}
                      placeholder="e.g., 32x32, 34x30"
                    />
                  ) : (
                    <p>{user?.pant_size || 'Not set'}</p>
                  )}
                </div>

                <div className="info-item">
                  <label>Shoe Size (US)</label>
                  {editing ? (
                    <input
                      type="text"
                      name="shoe_size"
                      value={profileData.shoe_size}
                      onChange={handleInputChange}
                      placeholder="e.g., 10, 10.5"
                    />
                  ) : (
                    <p>{user?.shoe_size || 'Not set'}</p>
                  )}
                </div>
              </div>

              <div className="info-item full-width">
                <label>Favorite Seasons</label>
                {editing ? (
                  <div className="season-selector">
                    {seasons.map(season => (
                      <button
                        key={season.value}
                        type="button"
                        className={`season-btn ${profileData.favorite_seasons?.includes(season.value) ? 'selected' : ''}`}
                        onClick={() => handleSeasonToggle(season.value)}
                      >
                        {season.emoji} {season.value.charAt(0).toUpperCase() + season.value.slice(1)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="season-display">
                    {user?.favorite_seasons && user.favorite_seasons.length > 0 ? (
                      user.favorite_seasons.map(season => {
                        const seasonData = seasons.find(s => s.value === season);
                        return (
                          <span key={season} className="season-badge">
                            {seasonData?.emoji} {season.charAt(0).toUpperCase() + season.slice(1)}
                          </span>
                        );
                      })
                    ) : (
                      <p>No favorite seasons selected</p>
                    )}
                  </div>
                )}
              </div>

              <div className="profile-actions">
                {editing ? (
                  <>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setEditing(false);
                        setError('');
                        setSuccess('');
                        // Reset to original values
                        setProfileData({
                          name: user?.name || '',
                          age: user?.age || '',
                          gender: user?.gender || '',
                          favorite_seasons: user?.favorite_seasons || [],
                          height: user?.height || '',
                          weight: user?.weight || '',
                          shirt_size: user?.shirt_size || '',
                          pant_size: user?.pant_size || '',
                          shoe_size: user?.shoe_size || ''
                        });
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={handleSaveProfile}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={() => setEditing(true)}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div className="stats-card">
            <h3>Account Statistics</h3>
            <div className="stat-item">
              <span className="stat-label">💎 Available Credits</span>
              <span className="stat-value">{user?.image_credits || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">📅 Member Since</span>
              <span className="stat-value">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">🔐 Auth Method</span>
              <span className="stat-value">
                {user?.auth_provider === 'google' ? '🔵 Google' : '📧 Email'}
              </span>
            </div>
          </div>

          {/* Logout Section */}
          <div className="logout-card">
            <h3>Account Actions</h3>
            <p>Sign out of your account</p>
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
